import type { ReactElement, ReactNode } from 'react';

import type { AppState } from '@/domain/models/appState';
import type { ConfigModel } from '@/domain/models/configModel';
import type { PersonModel } from '@/domain/models/personModel';
import type { SttConfigModel } from '@/domain/models/sttConfigModel';
import { formatVersion } from '@/domain/models/versionModel';
import { tokenIssuedAt } from '@/utils/diagnostics/tokenInfo';
import { NetworkStatus } from '@/utils/networkStatus';
import { StorageHelper } from '@/utils/storageHelper';

import styles from './StatusPanel.module.css';

/**
 * Перенос `_StatusPanel` из `diagnostics_page.dart`.
 *
 * Рядом с панелью живут и статические методы состояния экрана — `_status`,
 * `_issued`, `_time`, `_dateTime`, `_currentStt`: они считают ровно те строки,
 * которые панель рисует. `formatTime` нужен ещё и списку журнала — страница
 * берёт его отсюда и передаёт в строку пропом, как это делал Dart.
 */

/** Строка состояния: запись `(String, String, bool)` из Dart. */
export interface StatusRow {
  readonly name: string;
  readonly value: string;
  /** Значение, на которое инженеру стоит посмотреть: красное. */
  readonly bad: boolean;
}

const MINUTES_IN_HOUR = 60;
const MILLISECONDS_IN_MINUTE = 60_000;
const MILLISECONDS_IN_HOUR = 3_600_000;

/** `String two(int v) => v.toString().padLeft(2, '0')` */
function two(value: number): string {
  return value.toString().padStart(2, '0');
}

/** `_time` */
export function formatTime(time: Date): string {
  return `${two(time.getHours())}:${two(time.getMinutes())}:${two(time.getSeconds())}`;
}

/** `_dateTime` — месяц в Dart считается с единицы, в JS с нуля. */
export function formatDateTime(time: Date): string {
  return `${two(time.getDate())}.${two(time.getMonth() + 1)} ${formatTime(time)}`;
}

/**
 * `_issued`: когда выдан токен и сколько прошло. Сам токен не показывается.
 *
 * `Math.trunc` вместо `Math.floor` — `Duration.inHours` в Dart отбрасывает
 * дробную часть в сторону нуля, а не вниз.
 */
export function formatIssued(token: string | null): string {
  if (token === null) return 'нет';
  const at = tokenIssuedAt(token);
  if (at === null) return 'есть';
  const ageMs = Date.now() - at.getTime();
  const hours = Math.trunc(ageMs / MILLISECONDS_IN_HOUR);
  const minutes = Math.trunc(ageMs / MILLISECONDS_IN_MINUTE);
  const ago =
    hours >= 1
      ? `${hours} ч ${minutes % MINUTES_IN_HOUR} мин назад`
      : `${minutes} мин назад`;
  return `выдан ${formatDateTime(at)} (${ago})`;
}

/**
 * Сохранённый конфиг.
 *
 * В Dart `StorageHelper.instance.config` разбирался без защиты, и битое
 * значение бросало. Здесь исключение погашено намеренно: этот экран
 * открывают, когда на планшете уже что-то сломано, и сбой разбора унёс бы
 * вместе с панелью весь журнал — то единственное, ради чего сюда заходят.
 */
function storedConfig(): ConfigModel | null {
  try {
    return StorageHelper.instance.config;
  } catch {
    return null;
  }
}

/** `_currentStt`: STT выбранной персоны, сохранённый конфиг свежее снимка. */
export function currentStt(person: PersonModel | null): SttConfigModel | null {
  const stored = storedConfig()?.persons.find((p) => p.slug === person?.slug);
  return stored?.stt ?? person?.stt ?? null;
}

/** Значение строки «STT ключ». */
function sttKeyValue(stt: SttConfigModel | null): string {
  if (stt?.apiUrl == null) return '—';
  return stt.apiKey == null ? 'НЕТ — будет STT-401' : 'есть';
}

/** Первые семь строк `_status()`: сам планшет, его сеть и сессия. */
function tabletRows(appState: AppState): readonly StatusRow[] {
  const storage = StorageHelper.instance;
  const baseUrl = storage.baseUrl;
  const hasCreds = baseUrl !== null && storage.credentialsMatch(baseUrl);
  const offline = NetworkStatus.instance.isOffline;
  const token = storage.tabletToken;
  const refreshToken = storage.tabletRefreshToken;
  return [
    {
      name: 'Версия',
      value: formatVersion(appState.packageInfo.version.appVersion),
      bad: false,
    },
    { name: 'Сервер', value: baseUrl ?? 'не задан', bad: baseUrl === null },
    { name: 'Сеть', value: offline ? 'нет' : 'есть', bad: offline },
    {
      name: 'Сессия этого адреса',
      value: hasCreds ? 'есть' : 'нет',
      bad: !hasCreds,
    },
    {
      name: 'Пароль планшета',
      value: storage.tabletPassword === null ? 'нет (вход по PIN)' : 'сохранён',
      bad: false,
    },
    { name: 'token', value: formatIssued(token), bad: token === null },
    {
      name: 'refresh_token',
      value: formatIssued(refreshToken),
      bad: refreshToken === null,
    },
  ];
}

/** Последние четыре строки `_status()`: выбранный аватар и его STT. */
function personRows(person: PersonModel | null): readonly StatusRow[] {
  const stt = currentStt(person);
  return [
    {
      name: 'Аватар',
      value: person === null ? 'не выбран' : `${person.slug} (id ${person.id})`,
      bad: person === null,
    },
    { name: 'Нода', value: person?.personBaseUrl ?? '—', bad: false },
    {
      name: 'STT адрес',
      value: stt?.apiUrl ?? 'нет — микрофон выключен',
      bad: stt?.apiUrl == null,
    },
    {
      name: 'STT ключ',
      value: sttKeyValue(stt),
      bad: stt?.apiUrl != null && stt?.apiKey == null,
    },
  ];
}

/**
 * `_status()`: состояние планшета строками «название — значение».
 *
 * Порядок строк тот же, что в Dart; на две функции список разбит только
 * ради размера — одиннадцать строк в одной не помещаются.
 */
export function buildStatusRows(appState: AppState): readonly StatusRow[] {
  return [...tabletRows(appState), ...personRows(appState.person)];
}

export interface StatusPanelProps {
  readonly rows: readonly StatusRow[];
  readonly actions?: ReactNode[];
  /** Ширина панели приходит от страницы — это SizedBox(width: 620.w). */
  readonly className?: string;
}

export function StatusPanel({
  rows,
  actions = [],
  className,
}: StatusPanelProps): ReactElement {
  return (
    <div className={[styles.panel, className].filter(Boolean).join(' ')}>
      <div className={styles.scroll}>
        <div className={styles.sectionTitle}>Состояние</div>
        {rows.map((row) => (
          <div className={styles.row} key={row.name}>
            <div className={styles.name}>{row.name}</div>
            <div
              className={[styles.value, row.bad ? styles.valueBad : null]
                .filter(Boolean)
                .join(' ')}
            >
              {row.value}
            </div>
          </div>
        ))}
        {actions.length > 0 && (
          <>
            <div className={styles.actionsTitle}>Действия</div>
            {actions.map((action, index) => (
              <div className={styles.action} key={index}>
                {action}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
